import { inr, fmtDate } from "./format";

// Build a clean, print-optimised HTML document for the demand notice
// and open it in a new window so the user can Save as PDF.
export function downloadNoticePDF(invoice, calc, companyName) {
  const esc = (s = "") => String(s).replace(/[&<>"]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m]));
  const company = esc(companyName) || "the Supplier";
  const rows = `
    <tr><td>Principal outstanding</td><td class="r">${inr(invoice.principal)}</td></tr>
    <tr><td>Statutory interest (§16)</td><td class="r">${inr(calc.totalInterest)}</td></tr>
    <tr class="tot"><td>Total amount demanded</td><td class="r">${inr(calc.grandTotal)}</td></tr>`;

  const html = `<!doctype html><html><head><meta charset="utf-8"/>
  <title>Demand Notice ${invoice.id}</title>
  <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet"/>
  <style>
    @page { size: A4; margin: 22mm 20mm; }
    * { box-sizing: border-box; }
    body { font-family: 'IBM Plex Sans', sans-serif; color: #09090B; font-size: 12.5px; line-height: 1.65; margin: 0; }
    .mono { font-family: 'IBM Plex Mono', monospace; }
    .head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #09090B; padding-bottom:12px; }
    .title { font-family:'Cormorant Garamond',serif; font-weight:700; font-size:24px; letter-spacing:-0.02em; }
    .sub { font-family:'IBM Plex Mono',monospace; font-size:10px; letter-spacing:0.18em; text-transform:uppercase; color:#52525B; margin-top:4px; }
    .meta { text-align:right; font-family:'IBM Plex Mono',monospace; font-size:11px; color:#52525B; }
    .lbl { font-family:'IBM Plex Mono',monospace; font-size:10px; letter-spacing:0.1em; text-transform:uppercase; color:#52525B; }
    p { margin: 14px 0; }
    table.amt { width:100%; border:1px solid #D4D4D8; border-collapse:collapse; font-family:'IBM Plex Mono',monospace; font-size:12px; margin-top:8px; }
    table.amt td { padding:8px 12px; border-bottom:1px solid #D4D4D8; }
    table.amt td.r { text-align:right; }
    table.amt tr.tot td { background:#F4F4F5; font-weight:600; border-bottom:none; }
    .sign { margin-top:46px; }
    .disc { margin-top:34px; border-top:1px solid #D4D4D8; padding-top:10px; font-family:'IBM Plex Mono',monospace; font-size:8.5px; letter-spacing:0.12em; text-transform:uppercase; color:#52525B; line-height:1.7; }
    .b { font-weight:600; }
  </style></head><body>
    <div class="head">
      <div><div class="title">DEMAND NOTICE</div><div class="sub">Under Section 16, MSMED Act, 2006</div></div>
      <div class="meta">Ref: BAAKI/${invoice.id}<br/>Date: ${fmtDate(new Date().toISOString())}</div>
    </div>
    <p><span class="lbl">To,</span><br/><span class="b">${invoice.buyer}</span><br/><span style="color:#52525B">(Buyer / Purchaser — ${invoice.buyerType})</span></p>
    <p><span class="b">Subject:</span> Demand for payment of <span class="mono">${inr(invoice.principal)}</span> together with statutory interest for goods supplied / services rendered vide Invoice No. <span class="mono">${invoice.id}</span> dated ${fmtDate(invoice.invoiceDate)}.</p>
    <p>Our client, a Micro / Small Enterprise duly registered under the Udyam Registration framework, supplied goods / rendered services which were accepted by you on ${fmtDate(invoice.acceptanceDate)}. As per Section 15 of the MSMED Act, 2006, payment was due within the appointed day, not exceeding <span class="b">45 days</span> from the day of acceptance, i.e. on or before <span class="mono">${fmtDate(calc.due)}</span>.</p>
    <p>Despite the said period having elapsed, the principal amount remains unpaid for <span class="mono">${calc.daysOverdue}</span> days. Consequently, under Section 16, you are liable to pay compound interest with monthly rests at three times the Bank Rate notified by the Reserve Bank of India.</p>
    <table class="amt"><tbody>${rows}</tbody></table>
    <p>You are hereby called upon to pay the aforesaid sum within <span class="b">fifteen (15) days</span> of receipt of this notice, failing which our client shall be constrained to refer the dispute to the Micro and Small Enterprises Facilitation Council (MSEFC) via the Samadhaan portal under Section 18, and pursue all remedies available in law, entirely at your risk as to cost and consequence.</p>
    <div class="sign"><div class="b">For and on behalf of ${company}</div><div style="color:#52525B;font-size:11px">(Authorised Signatory)</div></div>
  </body></html>`;

  const w = window.open("", "_blank", "width=800,height=1000");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 600);
}

// Consolidated multi-invoice demand notice PDF — mirrors the emailed notice layout.
export function downloadConsolidatedNoticePDF(c, companyName) {
  const esc = (s = "") => String(s).replace(/[&<>"]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m]));
  const company = esc(companyName) || "the Supplier";
  const overdue = (c.invoices || []).filter((l) => l.daysSinceInvoice > 45);
  const totBal = overdue.reduce((a, l) => a + l.balance, 0);
  const totInt = overdue.reduce((a, l) => a + l.calc.totalInterest, 0);
  const grand = totBal + totInt;
  const rows = overdue.map((l) => `
    <tr>
      <td>${esc(l.invoiceNo)}</td>
      <td>${fmtDate(l.invoiceDate)}</td>
      <td>${l.daysSinceInvoice}d (${esc(l.ageBracket)})</td>
      <td class="r">${inr(l.balance)}</td>
      <td class="r">${inr(l.calc.totalInterest)}</td>
    </tr>`).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"/>
  <title>Consolidated Demand Notice — ${esc(c.buyer)}</title>
  <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet"/>
  <style>
    @page { size: A4; margin: 22mm 20mm; }
    * { box-sizing: border-box; }
    body { font-family: 'IBM Plex Sans', sans-serif; color: #09090B; font-size: 12.5px; line-height: 1.65; margin: 0; }
    .mono { font-family: 'IBM Plex Mono', monospace; }
    .head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #09090B; padding-bottom:12px; }
    .title { font-family:'Cormorant Garamond',serif; font-weight:700; font-size:24px; letter-spacing:-0.02em; }
    .sub { font-family:'IBM Plex Mono',monospace; font-size:10px; letter-spacing:0.18em; text-transform:uppercase; color:#52525B; margin-top:4px; }
    .meta { text-align:right; font-family:'IBM Plex Mono',monospace; font-size:11px; color:#52525B; }
    .lbl { font-family:'IBM Plex Mono',monospace; font-size:10px; letter-spacing:0.1em; text-transform:uppercase; color:#52525B; }
    p { margin: 14px 0; }
    table.amt { width:100%; border:1px solid #D4D4D8; border-collapse:collapse; font-family:'IBM Plex Mono',monospace; font-size:11.5px; margin-top:8px; }
    table.amt td, table.amt th { padding:7px 10px; border-bottom:1px solid #D4D4D8; text-align:left; }
    table.amt th { background:#F4F4F5; font-size:9px; letter-spacing:0.08em; text-transform:uppercase; }
    table.amt td.r, table.amt th.r { text-align:right; }
    table.amt tr.tot td { background:#F4F4F5; font-weight:600; }
    .sign { margin-top:46px; }
    .disc { margin-top:34px; border-top:1px solid #D4D4D8; padding-top:10px; font-family:'IBM Plex Mono',monospace; font-size:8.5px; letter-spacing:0.12em; text-transform:uppercase; color:#52525B; line-height:1.7; }
    .b { font-weight:600; }
  </style></head><body>
    <div class="head">
      <div><div class="title">DEMAND NOTICE</div><div class="sub">Under Section 16, MSMED Act, 2006</div></div>
      <div class="meta">Ref: BAAKI/${esc(c.id).slice(0, 8)}<br/>Date: ${fmtDate(new Date().toISOString())}</div>
    </div>
    <p><span class="lbl">To,</span><br/><span class="b">${esc(c.buyer)}</span><br/><span style="color:#52525B">(Buyer / Purchaser — ${esc(c.buyerType || "")})</span></p>
    <p>The following invoices supplied by our client, a Micro / Small Enterprise duly registered under the Udyam Registration framework, remain unpaid beyond the 45-day statutory period under Section 15 of the MSMED Act, 2006. You are liable under Section 16 to pay each balance together with compound interest (monthly rests) at three times the Bank Rate notified by the Reserve Bank of India.</p>
    <table class="amt">
      <thead><tr><th>Invoice</th><th>Date</th><th>Age</th><th class="r">Balance</th><th class="r">Interest</th></tr></thead>
      <tbody>
        ${rows}
        <tr class="tot"><td colspan="3">Grand total demanded</td><td class="r">${inr(totBal)}</td><td class="r">${inr(totInt)}</td></tr>
        <tr class="tot"><td colspan="4">Total (balance + interest)</td><td class="r">${inr(grand)}</td></tr>
      </tbody>
    </table>
    <p>You are hereby called upon to pay the aforesaid sum within <span class="b">fifteen (15) days</span> of receipt of this notice, failing which our client shall be constrained to refer the dispute to the Micro and Small Enterprises Facilitation Council (MSEFC) via the Samadhaan portal under Section 18, and pursue all remedies available in law, entirely at your risk as to cost and consequence.</p>
    <div class="sign"><div class="b">For and on behalf of ${company}</div><div style="color:#52525B;font-size:11px">(Authorised Signatory)</div></div>
  </body></html>`;

  const w = window.open("", "_blank", "width=800,height=1000");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 600);
}


// Print / save-as-PDF for an already-composed (possibly hand-edited) notice body.
export function printNoticeHtml(innerHtml, title = "Demand Notice") {
  const esc = (s = "") => String(s).replace(/[&<>"]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m]));
  const html = `<!doctype html><html><head><meta charset="utf-8"/><title>${esc(title)}</title>
  <style>
    @page { size:A4; margin:22mm 20mm; }
    body { font-family: Arial, Helvetica, sans-serif; color:#09090b; font-size:13px; line-height:1.7; margin:0; }
    table { border-collapse:collapse; }
    h1,h2,h3 { font-family: Georgia, 'Times New Roman', serif; }
    .disc { margin-top:34px; border-top:1px solid #d4d4d8; padding-top:10px; font-size:9px; letter-spacing:.12em; text-transform:uppercase; color:#666; line-height:1.7; }
  </style></head><body>
  ${innerHtml || ""}
  </body></html>`;
  const w = window.open("", "_blank", "width=800,height=1000");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 500);
}
